<template>
  <div class="basket-product-image">
    <div class="basket-product-image__container">
      <img
        :src="displayImage"
        :alt="name"
        loading="lazy"
        class="basket-product-image__img"
        @error="handleImageError"
        @load="handleImageLoad"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { consola } from 'consola'

interface Props {
  images?: any[]
  name: string
  placeholder?: string
}

const props = defineProps<Props>()

const logger = consola.withTag('BasketProductImage')

/**
 * Проверяет, является ли строка валидным URL изображения
 */
const isValidImageUrl = (url: string): boolean => {
  if (!url || typeof url !== 'string') return false

  const trimmedUrl = url.trim()
  if (trimmedUrl === '') return false

  // Проверяем, что это валидный URL
  try {
    const _parsed = new URL(trimmedUrl)
    void _parsed
  } catch {
    // Если не URL, проверяем что это относительный путь
    if (!trimmedUrl.startsWith('/') && !trimmedUrl.startsWith('./')) {
      return false
    }
  }

  // Проверяем расширения изображений
  const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp', '.tiff', '.ico']
  const lowerUrl = trimmedUrl.toLowerCase()

  return imageExtensions.some(ext => lowerUrl.includes(ext))
}

/**
 * Находит первую валидную картинку в массиве
 */
const displayImage = computed(() => {
  logger.log('🖼️ [BasketProductImage] Images array:', props.images)

  // Если есть массив изображений, ищем первое валидное
  if (Array.isArray(props.images) && props.images.length > 0) {
    for (let i = 0; i < props.images.length; i++) {
      const image = props.images[i]
      logger.log(`🖼️ [BasketProductImage] Checking image ${i}:`, image)

      // Проверяем разные варианты структуры объекта
      let imageUrl = null

      if (image && typeof image === 'object') {
        // Вариант 1: { url: '...' }
        if (image.url) {
          imageUrl = image.url
        }
        // Вариант 2: { src: '...' }
        else if (image.src) {
          imageUrl = image.src
        }
        // Вариант 3: { image: '...' }
        else if (image.image) {
          imageUrl = image.image
        }
        // Вариант 4: строка напрямую
      } else if (typeof image === 'string') {
        imageUrl = image
      }

      logger.log(`🖼️ [BasketProductImage] Extracted URL:`, imageUrl)

      if (imageUrl && isValidImageUrl(imageUrl)) {
        logger.log(`🖼️ [BasketProductImage] Found valid image:`, imageUrl)
        return imageUrl
      }
    }
  }

  logger.log('🖼️ [BasketProductImage] No valid images found, using placeholder')
  // Если не нашли валидную картинку, используем placeholder
  return props.placeholder || '/images/placeholder-product.png'
})

/**
 * Обрабатывает ошибку загрузки изображения
 */
const handleImageError = (event: Event) => {
  const img = event.target as HTMLImageElement
  logger.warn('🖼️ [BasketProductImage] Image failed to load:', img.src)

  // Если это не placeholder, пробуем загрузить placeholder
  if (!img.src.includes('placeholder')) {
    img.src = props.placeholder || '/images/placeholder-product.png'
  }
}

/**
 * Обрабатывает успешную загрузку изображения
 */
const handleImageLoad = (event: Event) => {
  const img = event.target as HTMLImageElement
  logger.log('🖼️ [BasketProductImage] Image loaded successfully:', img.src)
}
</script>

<style scoped lang="scss">
.basket-product-image {
  @apply w-12 h-12;

  &__container {
    @apply w-full h-full;
  }

  &__img {
    @apply w-full h-full object-cover;
    border-radius: var(--app-radius-media, 16px);
  }
}
</style>